import { useEffect, useMemo, useRef, useState } from "react"
import { formatBytes, parseAttachments } from "../../lib/attachments"
import type {
  ContentPart,
  FileAttachment,
  Message,
  ToolContentBlock,
  ToolState,
} from "../../lib/opencodeTypes"
import { errorText } from "../../lib/sessionEvents"
import { visibleContentParts } from "../../lib/sessionDisplay"
import { effortLabel } from "../../lib/slashCommands"
import { Markdown } from "../markdown"
import { SkeletonMessageList } from "./Skeleton"

interface MessageListProps {
  messages: Message[]
  loading: boolean
  thinking?: boolean
  showReasoning?: boolean
}

export function MessageList({
  messages,
  loading,
  thinking = false,
  showReasoning = false,
}: MessageListProps) {
  const bottomRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const pinnedRef = useRef(true)
  const firstLoadRef = useRef(true)

  // Text streams in a token at a time, so this runs on almost every frame of a
  // reply. Only follow the tail while the reader is already at it — otherwise
  // scrolling up to read earlier output yanks them back down mid-sentence.
  useEffect(() => {
    // The pinned heuristic reads scroll position, and on the very first
    // render of a loaded transcript that position is the top — which pinned
    // the page to the oldest message on every reload. Opening a conversation
    // lands at its end, like every chat does.
    if (firstLoadRef.current && messages.length > 0) {
      firstLoadRef.current = false
      bottomRef.current?.scrollIntoView({ block: "end" })
      return
    }
    const container = containerRef.current?.parentElement
    if (container) {
      const distance = container.scrollHeight - container.scrollTop - container.clientHeight
      pinnedRef.current = distance < 120
    }
    if (pinnedRef.current) bottomRef.current?.scrollIntoView({ block: "end" })
  }, [messages])

  if (loading && messages.length === 0) {
    return <SkeletonMessageList />
  }

  // While a turn is running the empty state would replace the only feedback the
  // user has that anything is happening — a first prompt in a new session shows
  // nothing else until the reply's first part arrives.
  if (messages.length === 0 && !thinking) {
    return (
      <div className="flex h-full flex-col items-center justify-center p-8 text-center">
        <div className="mb-3 text-4xl">🤖</div>
        <h3 className="mb-1 text-lg font-semibold text-foreground">Start a conversation</h3>
        <p className="max-w-sm text-sm text-muted">
          Type a message below to begin. The agent will help you with your code and answer questions.
        </p>
      </div>
    )
  }

  const lastId = messages[messages.length - 1]?.id

  return (
    <div ref={containerRef} className="mx-auto flex w-full max-w-3xl flex-col gap-5 p-4">
      {messages.map((msg, idx) => (
        // A stable key matters more than usual here: a key that changes per
        // render (Math.random()) remounts every bubble on each streamed token,
        // which loses scroll position and any expanded reasoning block.
        <MessageBubble
          key={msg.id ?? `idx-${idx}`}
          message={msg}
          streaming={thinking && msg.id === lastId}
          showReasoning={showReasoning}
        />
      ))}
      {thinking && <PendingIndicator messages={messages} showReasoning={showReasoning} />}
      <div ref={bottomRef} />
    </div>
  )
}

// Only shown before the first part of a reply exists — once text or a tool call
// starts rendering, that is the progress indicator.
function PendingIndicator({ messages, showReasoning }: { messages: Message[]; showReasoning: boolean }) {
  const last = messages[messages.length - 1]
  const hasContent =
    last?.type === "assistant" && visibleContentParts(last.content, showReasoning).length > 0
  if (hasContent) return null
  return (
    <div className="flex justify-start">
      <div className="flex items-center gap-2 rounded-lg bg-surface-muted px-4 py-2 text-sm text-muted">
        <div className="size-3 animate-spin rounded-full border-2 border-border border-t-accent" />
        Thinking…
      </div>
    </div>
  )
}

function MessageBubble({
  message,
  streaming,
  showReasoning,
}: {
  message: Message
  streaming: boolean
  showReasoning: boolean
}) {
  if (message.type === "user") return <UserBubble text={message.text} files={message.files} />

  if (message.type === "assistant") {
    const parts = visibleContentParts(message.content, showReasoning)
    // A step that produced nothing yet renders as nothing; the pending
    // indicator below the list covers that gap.
    if (parts.length === 0 && !message.error) return null
    return (
      <div className="flex justify-start">
        <div className="w-full max-w-[92%] px-1 py-0.5">
          {parts.map((part, idx) => (
            <ContentPartRenderer key={part.id ?? idx} part={part} streaming={streaming} />
          ))}
          {message.error != null && (
            <div className="mt-2 rounded bg-danger-soft px-3 py-2 text-sm text-danger">
              {errorText(message.error)}
            </div>
          )}
        </div>
      </div>
    )
  }

  if (message.type === "system") {
    return (
      <div className="flex justify-center">
        <div className="rounded bg-surface-muted px-3 py-1.5 text-xs text-muted">{message.text}</div>
      </div>
    )
  }

  if (message.type === "synthetic") {
    return (
      <div className="flex justify-center">
        <div className="rounded bg-surface-muted px-3 py-1.5 text-xs text-muted">{message.text}</div>
      </div>
    )
  }

  if (message.type === "compaction") {
    return (
      <div className="flex justify-center">
        <div className="rounded bg-surface-muted px-3 py-1.5 text-xs text-muted">
          History compacted{message.reason ? ` (${message.reason})` : ""}
        </div>
      </div>
    )
  }

  if (message.type === "agent-switched") {
    return (
      <div className="flex justify-center">
        <div className="rounded bg-surface-muted px-3 py-1.5 text-xs text-muted">
          Switched to agent {message.agent}
        </div>
      </div>
    )
  }

  if (message.type === "model-switched") {
    return (
      <div className="flex justify-center">
        <div className="rounded bg-surface-muted px-3 py-1.5 text-xs text-muted">
          Switched to model {message.model?.id}
          {message.model?.variant ? ` · ${effortLabel(message.model.variant)}` : ""}
        </div>
      </div>
    )
  }

  if (message.type === "shell") {
    return (
      <div className="flex justify-start">
        <div className="max-w-[80%] rounded-lg bg-surface px-4 py-2 font-mono text-xs">
          <div className="text-muted">$ {message.command}</div>
          {message.output && <div className="mt-1 whitespace-pre-wrap text-foreground">{message.output}</div>}
        </div>
      </div>
    )
  }

  return null
}

function ContentPartRenderer({ part, streaming }: { part: ContentPart; streaming: boolean }) {
  if (part.type === "text") {
    // Markdown even while streaming — the same renderer the wiki trusts, so
    // the same escaping rules hold. A half-open ``` fence renders the tail as
    // a growing code block until the closing fence arrives, which reads as
    // progress rather than flicker; plain text instead meant every table in a
    // long reply showed as raw pipes until the turn finished.
    return (
      <div className="text-sm">
        <Markdown className="text-foreground">{part.text}</Markdown>
        {streaming && part.text.length > 0 && <StreamingCaret />}
      </div>
    )
  }

  if (part.type === "reasoning") {
    return <ReasoningPartRenderer part={part} live={streaming} />
  }

  if (part.type === "tool") {
    return <ToolPartRenderer part={part} />
  }

  return null
}

function StreamingCaret() {
  return <span className="ml-0.5 inline-block h-3.5 w-1.5 animate-pulse bg-accent align-middle" />
}

// This renderer is reached only when the user has enabled reasoning in the
// Context panel. Historical blocks start collapsed; a live block opens while
// it streams so the enabled view still gives useful progress feedback.
function ReasoningPartRenderer({ part, live }: { part: Extract<ContentPart, { type: "reasoning" }>; live: boolean }) {
  // Collapsed by default, like the desktop: the reply is the point, the
  // thinking is there for whoever asks. A block still streaming opens itself
  // so a turn that is "only thinking" shows visible progress.
  const [open, setOpen] = useState(false)
  // `complete` is a client-reducer flag set on reasoning.ended, so parts
  // loaded from history never carry it — gating on the message being live is
  // what stops every reloaded block pulsing forever.
  const streaming = live && part.complete !== true
  const expanded = open || streaming

  return (
    <div className="my-1.5 rounded-lg border border-border/40 bg-surface/40 px-3 py-1.5">
      <button
        onClick={() => setOpen(!open)}
        className="flex w-full items-center gap-2 text-left text-xs font-medium text-muted hover:text-foreground"
      >
        <span className={`transition-transform ${expanded ? "rotate-90" : ""}`}>›</span>
        Reasoning
        {streaming && (
          <span className="size-2 animate-pulse rounded-full bg-accent" aria-label="streaming" />
        )}
      </button>
      {expanded && (
        <div className="mt-2 whitespace-pre-wrap border-l-2 border-border pl-3 text-xs italic text-muted">
          {part.text}
          {streaming && part.text.length > 0 && <StreamingCaret />}
        </div>
      )}
    </div>
  )
}

// Tool output is a list of content blocks. There is no `state.output` and no
// `state.title` on the wire — reading those rendered "(no output)" for every
// tool that had in fact returned something.
function toolText(content: ToolContentBlock[] | undefined): string {
  if (!content || content.length === 0) return ""
  return content
    .map((block) => block.text ?? "")
    .filter(Boolean)
    .join("\n")
}

// The *stored* transcript is emptier than the live one: on reload, completed
// tools come back with `content: []` and their real result only in
// `structured` — tool-specific JSON (confirmed against a live container). The
// desktop app has a renderer per tool; here one readable fallback covers them
// all rather than showing "(no output)" for every reloaded card.
function structuredText(structured: unknown): string {
  if (structured === null || structured === undefined) return ""
  if (typeof structured === "string") return structured
  if (typeof structured !== "object") return String(structured)

  const record = structured as Record<string, unknown>
  // The shapes tools actually use for their main payload, before giving up
  // and showing JSON: a text-ish field, or a list of paths.
  for (const key of ["text", "output", "content", "stdout"]) {
    if (typeof record[key] === "string" && record[key]) return record[key] as string
  }
  if (Array.isArray(record["entries"])) {
    const paths = record["entries"]
      .map((entry) => (entry && typeof entry === "object" ? String((entry as Record<string, unknown>)["path"] ?? "") : ""))
      .filter(Boolean)
    if (paths.length > 0) return paths.join("\n")
  }
  try {
    const json = JSON.stringify(structured, null, 2)
    return json === "{}" ? "" : json.slice(0, 4_000)
  } catch {
    return ""
  }
}

function summarise(input: unknown): string {
  if (input === null || input === undefined) return ""
  if (typeof input === "string") return input
  try {
    return JSON.stringify(input)
  } catch {
    return String(input)
  }
}

function ToolPartRenderer({ part }: { part: Extract<ContentPart, { type: "tool" }> }) {
  const state: ToolState = part.state
  const input = "input" in state ? summarise(state.input) : ""
  const output =
    ("content" in state ? toolText(state.content) : "") ||
    ("structured" in state ? structuredText(state.structured) : "")

  return (
    <div className="my-2 rounded-lg border border-border/60 bg-surface/60 p-2.5">
      <div className="flex items-center gap-2 text-xs font-medium">
        <span className="text-foreground">{part.name || "Unknown Tool"}</span>
        {(state.status === "running" || state.status === "pending") && (
          <div className="size-3 animate-spin rounded-full border-2 border-border border-t-accent" />
        )}
        {state.status === "completed" && <span className="text-success">✓</span>}
        {state.status === "error" && <span className="text-danger">✗</span>}
      </div>

      {input && (
        <div className="mt-1 truncate font-mono text-xs text-muted" title={input}>
          {input}
        </div>
      )}

      {state.status === "completed" && (
        <div className="mt-2">
          <details>
            <summary className="cursor-pointer text-xs text-muted">Output</summary>
            <pre className="mt-1 max-h-40 overflow-auto rounded bg-surface-muted p-2 text-xs">
              {output || "(no output)"}
            </pre>
          </details>
        </div>
      )}

      {state.status === "running" && output && (
        <pre className="mt-2 max-h-32 overflow-auto rounded bg-surface-muted p-2 text-xs">{output}</pre>
      )}

      {state.status === "error" && (
        <div className="mt-1 text-xs text-danger">
          {state.error == null ? "Unknown error" : errorText(state.error)}
        </div>
      )}
    </div>
  )
}

// Attachments are inlined into the text rather than sent as file parts (see
// lib/attachments.ts), so the text *is* the only record of them — which is why
// one code path serves both the live echo and reloaded history.
//
// Its own component so the parse can be memoised: an assistant reply streaming
// in re-renders the whole list per delta, and re-scanning every attachment body
// each time is work proportional to the transcript.
function UserBubble({ text, files }: { text: string; files?: FileAttachment[] }) {
  const parsed = useMemo(() => parseAttachments(text), [text])
  const images = files?.filter((file) => file.mime.startsWith("image/")) ?? []
  return (
    <div className="flex justify-end">
      <div className="max-w-[80%] rounded-lg bg-surface-muted px-3 py-1.5 text-sm text-foreground">
        {images.length > 0 && (
          <div className="mb-2 flex flex-wrap gap-2">
            {images.map((file, index) => (
              // The src is the same data URI that was sent, which history hands
              // back in full on every fetch — so rendering it costs nothing
              // extra, but a session with many images makes that fetch large.
              // An upload endpoint with opaque URLs is the real fix, and a much
              // bigger change than this one.
              <a
                key={`${file.name ?? "image"}-${index}`}
                href={file.uri}
                target="_blank"
                rel="noreferrer"
                title={file.name}
              >
                <img
                  src={file.uri}
                  alt={file.name ?? "Attached image"}
                  className="max-h-40 rounded border border-accent-foreground/20 object-contain"
                />
              </a>
            ))}
          </div>
        )}
        {parsed.blocks.length > 0 && (
          <div className="mb-2 flex flex-col gap-1">
            {parsed.blocks.map((block, index) => (
              <AttachmentChip key={`${block.name}-${index}`} name={block.name} body={block.body} />
            ))}
          </div>
        )}
        {parsed.text && <div className="whitespace-pre-wrap text-sm">{parsed.text}</div>}
      </div>
    </div>
  )
}

// Collapsed by default: the whole point of a chip is that the transcript does
// not become the file.
function AttachmentChip({ name, body }: { name: string; body: string }) {
  const [open, setOpen] = useState(false)
  const size = useMemo(() => new TextEncoder().encode(body).length, [body])
  return (
    <div className="rounded border border-accent-foreground/20 bg-black/10 text-xs">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="flex w-full items-baseline justify-between gap-3 px-2 py-1 text-left"
      >
        <span className="font-medium">{name}</span>
        <span className="opacity-70">
          {formatBytes(size)} · {open ? "hide" : "show"}
        </span>
      </button>
      {open && (
        <pre className="max-h-48 overflow-auto border-t border-accent-foreground/20 px-2 py-1 font-mono whitespace-pre">
          {body}
        </pre>
      )}
    </div>
  )
}
